/**
 * FurnitureUseModule — FUNCTIONAL FURNITURE (2026-09-24).
 *
 * Inside a building every piece with a function (lib/furniture_functions.ts) is usable within
 * FURNITURE_USE_M: the nearest one claims the E key through the interact arbiter (the door and
 * the structure console claim too; the nearest wins) and shows its prompt.
 *   · storage  — the owner opens a window over the container (structure_container +
 *                container_item, row-level secured to the owner) and their inventory: Store / Take;
 *                anyone else reads "locked" and E toasts the rule.
 *   · sit / lie — the avatar walks the last metre onto the piece (the auto-walk stick, so the
 *                server row comes along), parks, the eye drops, the body reads as seated; any move
 *                key or E stands up. Lying refills the breath.
 *   · toggle   — lamps, the hearth, the holo-screen: a glow sprite over the piece.
 *   · craft / market / passport — open the Workbench, its market tab, the Passport.
 *   · music    — the orchestrion cycles the house's tunes (AtlasMusicModule's jukebox).
 *   · garden   — plant a food family from a stack; harvest threefold when ready.
 *   · guestbook — anyone signs; the book keeps the last forty lines.
 * Pieces come from two places: crafted `structure_item` rows (world pose from the row + the
 * structure's seat, as DecorationModule composes them) and furnish-pack clones under the interior
 * group (tagged by BuildingInteriorModule). Absolute coordinates throughout (the frame law).
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import type { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { keyIs } from '../lib/keybinds.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { softRender, fieldByKey } from '../lib/soft_render.js';
import { enterables } from '../lib/building_interiors.js';
import { structureSeats } from '../lib/structure_seat.js';
import { FURNITURE } from '../lib/furniture_catalog.js';
import { interactFocus } from '../lib/interact_focus.js';
import { unitKeys } from '../lib/rental_client.js'; // RENTALS (the interiors programme, slice R, 2026-10-01)
import { allLeases, allUnits, isMine, tenantContainer, tenantEntries, rentalReducers } from './rentals/rental_rows.js';
import {
  furnitureFunction, useLabel, seatPose, containerKeyFor, FURNITURE_USE_M, CONTAINER_CAPACITY, CONTAINER_FILTER, PLANTER_YIELD, seatReach,
  type FurnitureFunction,
} from '../lib/furniture_functions.js';
import { sfx } from '../lib/sfx.js';
import { activeInterior, activeDescriptorFor, pieceUseOf, keyedOffer } from '../lib/interior_wiring.js';
import type { AtlasInputModule } from './AtlasInputModule.js';
import { venueCounter } from '../lib/venue_client.js';

/** One press, two routes (the E key and the input module's `interact` bus event): the second inside this window is the same press. */
const PRESS_DEDUPE_MS = 150;

/** The orchestrion's tunes, in order (the installed music keys). */
export const JUKEBOX_TRACKS = ['music_town_1', 'music_town_2', 'music_wander_1', 'music_wander_2', 'music_wander_3', 'music_night', 'music_city_1', 'music_space_1', 'music_menu'];

interface Piece {
  id: string;            // 'item:<structureItemId>' | 'pack:<f>:<i>:<key>'
  key: string;           // furniture key
  fn: FurnitureFunction;
  x: number; y: number; z: number; yaw: number;
  height: number;
  structureId: bigint | null;
  itemId: bigint | null;
  /** RENTALS (slice R): set on a storage piece of MY let room — the E opens the tenant's own container (lease id). */
  lease?: bigint;
  /** THE E KEY MEETS THE ANCHOR (interiors programme, slice C, 2026-10-01) — a PLANNED piece's own approach points
   *  (where a user stands: cells the planner's capsule route reaches) and sockets (where a body sits / lies), in
   *  absolute world coordinates. Absent on a crafted piece and on a legacy slot (the centre-only offer, as before). */
  approach?: Array<{ x: number; z: number }>;
  sockets?: Array<{ x: number; z: number; yaw: number }>;
}

/** How far above / below the feet a piece may be and still be offered (m): a storey is 3.4 m — the chest on the floor
 *  above is not "the nearest piece". Applied in a planned room only (the lever keeps the old XZ-only offer). */
const FLOOR_BAND_UP_M = 2.2, FLOOR_BAND_DOWN_M = 1.4;

interface StructureRow { structureId: bigint; owner: Identity; posX: number; posZ: number; yaw: number; worldId: bigint }
interface ItemRow { itemId: bigint; structureId: bigint; prefabId: string; lx: number; ly: number; lz: number; yaw: number }
interface ContainerRow { containerId: bigint; structureId: bigint; key: string; kind: string; capacity: number }
interface EntryRow { entryId: bigint; containerId: bigint; kind: string; itemKey: string; name: string; quality: number; qty: number }
interface PlayerItemRow { itemId: bigint; owner: Identity; itemKey: string; name: string; quality: number; serial: string }
interface StackRow { stackId: bigint; owner: Identity; familyKey: string; name: string; qty: number }
interface PlanterRow { planterId: bigint; structureId: bigint; key: string; familyKey: string; name: string; readyAt: { microsSinceUnixEpoch: bigint } }
interface NoteRow { noteId: bigint; structureId: bigint; authorName: string; text: string; at: { microsSinceUnixEpoch: bigint } }

/** Is this planned piece the service counter / desk of the bound venue the player stands in (slice E)? */
function venueServicePiece(interior: string, floor: number | undefined, slot: string | undefined): boolean {
  const vc = venueCounter();
  if (!vc || vc.id !== interior || !slot || (floor ?? 0) !== 0) return false;
  return vc.anchors.staff.some((q) => q.piece === slot) || vc.anchors.customer.some((q) => q.piece === slot);
}

export class FurnitureUseModule implements IModule {
  readonly name = 'FurnitureUse';

  private app!: App;
  private scene!: THREE.Scene;
  private prompt: HTMLElement | null = null;
  private nearest: Piece | null = null;
  private pieces: Piece[] = [];
  private scanAt = 0;
  private toggles = new Set<string>();
  private glows = new Map<string, THREE.Sprite>();
  private glowMat: THREE.SpriteMaterial | null = null;
  private win: UiWindow | null = null;
  private winPiece: Piece | null = null;
  private renderTimer: number | null = null;
  private subscribed = false;
  private walking: { piece: Piece; fn: 'sit' | 'lie'; since: number; socket: { x: number; z: number; yaw: number } | null; stand: { x: number; z: number } | null } | null = null;
  private jukeboxIdx = -1;
  private onKey = (e: KeyboardEvent): void => this.key(e);

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    const p = document.createElement('div');
    p.id = 'furniture-prompt';
    p.style.cssText = 'position:fixed;left:50%;bottom:262px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg2) 86%, transparent);border:1px solid var(--ax-line2);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    document.body.appendChild(p);
    this.prompt = p;
    window.addEventListener('keydown', this.onKey);
    bus.on('atlas:place-requested', () => { const t = document.activeElement as HTMLElement | null; if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return; this.press(); }); // (slice H) the pad's interact
    app.world.addSystem(makeSystem('FurnitureUse', Phase.UI, 37, () => this.tick()));
    app.world.addSystem(makeSystem('FurnitureSeatWalk', Phase.FixedSim, 38, () => this.seatWalk()));
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    bus.on('atlas:world-cleared', () => { this.standUp(); this.win?.setOpen(false); });
    (window as unknown as { __furniture?: unknown }).__furniture = () => this.diag();
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subscribed) return;
    this.subscribed = true;
    try {
      conn.subscriptionBuilder().subscribe([
        'SELECT * FROM structure_container', 'SELECT * FROM container_item', // row-level secured: only my structures' rows arrive
        'SELECT * FROM structure_planter', 'SELECT * FROM structure_note',
      ]);
    } catch { /* pre-publish */ }
    const db = conn.db as unknown as Record<string, { onInsert?(cb: () => void): void; onUpdate?(cb: () => void): void; onDelete?(cb: () => void): void } | undefined>;
    for (const name of ['containerItem', 'structureContainer', 'playerItem', 'playerResource', 'structurePlanter', 'structureNote', 'structureItem', 'rentalContainer', 'rentalStored', 'rentalLease', 'rentalUnit']) {
      const t = db[name];
      const ping = (): void => { this.scanAt = 0; this.scheduleRender(); };
      t?.onInsert?.(ping); t?.onUpdate?.(ping); t?.onDelete?.(ping);
    }
  }

  // ─── The pieces in reach ────────────────────────────────────────────────────

  private scan(): void {
    const conn = atlas.conn;
    const interior = atlas.interiorId;
    const out: Piece[] = [];
    if (!conn || !interior) { this.pieces = out; return; }
    const deed = interior.startsWith('deed:');
    const structureId = deed ? BigInt(interior.slice(5)) : null;
    const db = conn.db as unknown as { structure: { structureId: { find(id: bigint): StructureRow | undefined } }; structureItem: { iter(): Iterable<ItemRow> } };
    if (structureId !== null) {
      const s = db.structure.structureId.find(structureId);
      const seatY = structureSeats.get(structureId.toString())?.y;
      if (s && seatY !== undefined) {
        const cos = Math.cos(s.yaw), sin = Math.sin(s.yaw);
        for (const it of db.structureItem.iter()) {
          if (it.structureId !== structureId || !it.prefabId.startsWith('furn_')) continue;
          const key = it.prefabId.slice(5);
          const fn = furnitureFunction(key);
          if (!fn) continue;
          out.push({
            id: `item:${it.itemId}`, key, fn,
            x: s.posX + it.lx * cos + it.lz * sin, y: seatY + it.ly, z: s.posZ - it.lx * sin + it.lz * cos,
            // ITEM 8b (slice R, 2026-10-01): `structure_item.yaw` is RADIANS on the wire — the crafting window sends
            // `atlas.localYaw` and DecorationModule draws `Euler(pitch, yaw, roll)`. This read took it for DEGREES, so a
            // crafted chair turned 90° sat its user facing 1.6° off the structure's axis — beside the drawn seat's front.
            yaw: s.yaw + it.yaw, height: FURNITURE[key]?.h ?? 1, structureId, itemId: it.itemId,
          });
        }
      }
    }
    // Furnish-pack (and versona-home) clones under the interior group.
    const group = this.scene.getObjectByName('building-interior');
    if (group) {
      // THE LAYOUT PIN (slice C): a pack clone's storage key is the one the visit tagged it with (`packView` — the
      // legacy index on a layout-0 room, the rank among its key on a planned one). A room NOT drawn at its structure's
      // pinned layout (the lever on a structure pinned ≥ 1) offers no keyed function: one layout is never keyed as another.
      const act = activeInterior();
      const keyedOk = !act || act.id !== interior || act.keyedOk;
      const planned = activeDescriptorFor(interior);
      const b = planned ? enterables.get(interior) : undefined;
      const bc = b ? Math.cos(b.yaw) : 1, bs = b ? Math.sin(b.yaw) : 0;
      // THE LIVE STORAGE (interiors programme C2, 2026-10-01): the rows the server's caps are counted over, read once
      // a scan (the owner's — row security hides them from everyone else).
      const keyed = !!act?.planner && structureId !== null ? this.keyedRows(structureId) : null;
      const letKeys = structureId !== null ? this.letKeys(structureId) : null; // RENTALS: the keyed pieces of this structure's let rooms
      for (const o of group.children) {
        const key = o.userData.furnKey as string | undefined;
        if (!key) continue;
        const fn = furnitureFunction(key);
        if (!fn) continue;
        if (fn === 'storage' && structureId === null) continue; // storage is a player structure's
        // THE VENUE COUNTER (slice E): the desk a bound venue's post is kept at is the post's — a clinic's reception
        // desk is the healer's counter, not a bazaar terminal (its E belongs to the venue module's claim).
        if (fn === 'market' && venueServicePiece(interior, o.userData.furnFloor as number | undefined, o.userData.furnSlot as string | undefined)) continue;
        // A `pack:<floor>:<index>:<key>` piece is a FURNISH-PACK clone (2026-09-25): the guild hall's own furnishing
        // carries no floor/index, so every bookcase keyed `pack:0:0:bookcase` — one shared container, and one the
        // server refuses without a pack row. Keyed functions (storage, the planter) stand on pack clones only.
        if ((fn === 'storage' || fn === 'garden') && structureId !== null && (!o.userData.packFurniture || !keyedOk)) continue;
        const pieceId = (o.userData.furnStorageKey as string | undefined) ?? containerKeyFor({ floor: o.userData.furnFloor as number, index: o.userData.furnIndex as number, key });
        // A storage piece the room did not mark LIVE, or one the server has no room for, is PLAIN FURNITURE: no prompt
        // at all — never one that fails, never "on display" (the caps stay the server's; a piece whose container
        // already exists always opens). Under the lever the prompt is today's.
        // RENTALS (slice R, 2026-10-01): a let room's keyed pieces and its bed are the TENANT's. To the tenant the
        // storage opens as their own (rows only they are handed — the owner's caps and "live" marks do not apply); to
        // everyone else, the landlord included, those pieces are plain furniture: never a prompt the server refuses.
        const letTo = letKeys?.get(pieceId);
        if (letTo && (letTo.mine ? fn === 'garden' : fn === 'storage' || fn === 'garden' || fn === 'lie')) continue;
        const tenantStore = !!letTo?.mine && fn === 'storage';
        if (!tenantStore && (fn === 'storage' || fn === 'garden') && keyed && !keyedOffer({ fn, key: pieceId, kind: key, live: o.userData.furnStorageLive !== false, containers: keyed.containers, planters: keyed.planters })) continue;
        const piece: Piece = {
          id: pieceId,
          key, fn, x: o.position.x, y: o.position.y, z: o.position.z, yaw: o.rotation.y, height: FURNITURE[key]?.h ?? 1, structureId, itemId: null,
        };
        if (tenantStore) piece.lease = letTo!.leaseId;
        const slot = o.userData.furnSlot as string | undefined;
        if (planned && b && slot) {
          const use = pieceUseOf(planned.descriptor!, o.userData.furnFloor as number, slot);
          const toW = (lx: number, lz: number): { x: number; z: number } => ({ x: b.x + lx * bc + lz * bs, z: b.z - lx * bs + lz * bc });
          if (use.approach.length) piece.approach = use.approach.map((q) => toW(q.x, q.z));
          if (use.sockets.length) piece.sockets = use.sockets.map((q) => ({ ...toW(q.x, q.z), yaw: b.yaw + q.yaw }));
        }
        out.push(piece);
      }
    }
    this.pieces = out;
  }

  private tick(): void {
    const now = performance.now();
    if (now - this.scanAt > 500) { this.scanAt = now; this.scan(); }
    const focus = interactFocus();
    let best: Piece | null = null, bestD = FURNITURE_USE_M;
    if (!atlas.seated) {
      const floorBand = !!activeInterior()?.planner; // a planned room: only what stands on the floor you are on
      for (const p of this.pieces) {
        if (floorBand && (p.y - atlas.localY > FLOOR_BAND_UP_M || atlas.localY - p.y > FLOOR_BAND_DOWN_M)) continue;
        const d = this.offerDist(p);
        if (d < bestD) { bestD = d; best = p; }
      }
    }
    this.nearest = best;
    if (best) focus.claim({ id: `furn:${best.id}`, dist: bestD, priority: 2, label: this.labelFor(best) });
    const owns = !!best && focus.owns(`furn:${best.id}`);
    const showing = owns && !this.win?.isOpen();
    if (this.prompt) {
      const cur = this.prompt.style.display !== 'none';
      if (showing !== cur) this.prompt.style.display = showing ? 'block' : 'none';
      if (showing && best) { const l = this.labelFor(best); if (this.prompt.textContent !== l) this.prompt.textContent = l; }
    }
    if (atlas.seated && this.prompt && this.prompt.style.display === 'none') { this.prompt.style.display = 'block'; this.prompt.textContent = 'E — stand up'; }
    this.tickGlows();
  }

  /** How far the player is from USING a piece: its centre, or — a planned piece — the nearest of its approach points
   *  (a long counter or a bed is used from its side, metres from its centre). */
  private offerDist(p: Piece): number {
    let d = Math.hypot(p.x - atlas.localX, p.z - atlas.localZ);
    if (p.approach) for (const a of p.approach) { const q = Math.hypot(a.x - atlas.localX, a.z - atlas.localZ); if (q < d) d = q; }
    return d;
  }

  /** Open a pack container / planter whose piece the room no longer seats (the console's list — interiors programme,
   *  slice C): whatever a layout change left behind can always be taken out. The server needs only the key's shape. */
  async openUnseated(structureId: bigint, key: string, kind: string): Promise<void> {
    const fn = furnitureFunction(kind);
    if (fn !== 'storage' && fn !== 'garden') return;
    const p: Piece = { id: key, key: kind, fn, x: atlas.localX, y: atlas.localY, z: atlas.localZ, yaw: 0, height: FURNITURE[kind]?.h ?? 1, structureId, itemId: null };
    if (fn === 'storage') {
      try {
        await (atlas.conn!.reducers as unknown as { openContainer(a: { structureId: bigint; key: string; kind: string }): Promise<void> }).openContainer({ structureId, key, kind });
      } catch (err) { bus.emit('atlas:toast', { text: String((err as Error).message ?? err).replace(/^SenderError:\s*/, ''), kind: 'error' }); return; }
    }
    this.open(p);
  }

  private labelFor(p: Piece): string {
    const name = FURNITURE[p.key]?.name ?? p.key.replace(/_/g, ' ');
    if (p.fn === 'storage' && p.lease === undefined && !this.isOwner(p)) return `${name} — the owner's storage`;
    const planter = p.fn === 'garden' ? this.planterOf(p) : null;
    return useLabel(p.fn, name, {
      on: this.toggles.has(p.id) || (p.fn === 'music' && this.jukeboxIdx >= 0),
      planted: !!planter, ready: !!planter && Number(planter.readyAt.microsSinceUnixEpoch / 1000n) <= Date.now(),
    });
  }

  /** The structure's container and planter rows this client sees — what `keyedOffer` counts the server's caps over
   *  (16 pack containers, 6 of a kind, 6 crops). The scan drops a piece the rule does not offer, so no prompt ever
   *  names storage the server would refuse. */
  private keyedRows(structureId: bigint): { containers: Array<{ key: string; kind: string }>; planters: string[] } {
    const out = { containers: [] as Array<{ key: string; kind: string }>, planters: [] as string[] };
    const db = atlas.conn?.db as unknown as { structureContainer?: { iter(): Iterable<ContainerRow> }; structurePlanter?: { iter(): Iterable<PlanterRow> } } | undefined;
    try {
      if (db?.structureContainer) for (const c of db.structureContainer.iter()) if (c.structureId === structureId) out.containers.push({ key: c.key, kind: c.kind });
      if (db?.structurePlanter) for (const r of db.structurePlanter.iter()) if (r.structureId === structureId) out.planters.push(r.key);
    } catch { /* table mid-churn: an empty read offers the live pieces, and the server still judges */ }
    return out;
  }

  /** RENTALS (slice R): the keyed pieces of this structure's LET rooms → the lease, and whether it is mine. */
  private letKeys(structureId: bigint): Map<string, { leaseId: bigint; mine: boolean }> | null {
    const leases = allLeases().filter((l) => l.structureId === structureId && l.state === 'live');
    if (leases.length === 0) return null;
    const units = allUnits();
    const out = new Map<string, { leaseId: bigint; mine: boolean }>();
    for (const l of leases) {
      const u = units.find((x) => x.unitRowId === l.unitRowId);
      if (u) for (const k of unitKeys(u)) out.set(k, { leaseId: l.leaseId, mine: isMine(l) });
    }
    return out;
  }

  private isOwner(p: Piece): boolean {
    const conn = atlas.conn;
    if (!conn?.identity || p.structureId === null) return false;
    const s = (conn.db as unknown as { structure: { structureId: { find(id: bigint): StructureRow | undefined } } }).structure.structureId.find(p.structureId);
    return !!s && s.owner.isEqual(conn.identity);
  }

  private planterOf(p: Piece): PlanterRow | null {
    const conn = atlas.conn;
    if (!conn || p.structureId === null) return null;
    const db = conn.db as unknown as { structurePlanter?: { iter(): Iterable<PlanterRow> } };
    if (!db.structurePlanter) return null;
    for (const r of db.structurePlanter.iter()) if (r.structureId === p.structureId && r.key === p.id) return r;
    return null;
  }

  // ─── E ──────────────────────────────────────────────────────────────────────

  private key(e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey || e.repeat) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
    if (atlas.seated) {
      if (keyIs(e.code, 'interact')) { this.press(); return; }
      // The BOUND movement keys stand you up (literal W/A/S/D/Space left a rebound layout with only Interact to rise).
      if (keyIs(e.code, 'moveForward') || keyIs(e.code, 'moveBack') || keyIs(e.code, 'moveLeft') || keyIs(e.code, 'moveRight') || keyIs(e.code, 'jump')) { this.standUp(); }
      return;
    }
    if (!keyIs(e.code, 'interact')) return; // the BOUND key: a rebound E still used the piece
    this.press();
  }

  /** THE PAD'S INTERACT (slice H, 2026-10-02): a pad or a rebound interact arrives only as `atlas:place-requested` —
   *  a pad player could not sit, open a chest or stand up. The keyboard's E arrives BOTH ways (the input module emits
   *  the bus event for its `interact` key too), so a press inside `PRESS_DEDUPE_MS` of the last is the same press. */
  private lastPress = -1e9;
  private press(): void {
    const now = performance.now();
    if (now - this.lastPress < PRESS_DEDUPE_MS) return;
    this.lastPress = now;
    if (atlas.seated) { this.standUp(); return; }
    const p = this.nearest;
    if (!p || !interactFocus().owns(`furn:${p.id}`)) return;
    if (this.win?.isOpen()) { this.win.setOpen(false); return; }
    void this.use(p);
  }

  private async use(p: Piece): Promise<void> {
    switch (p.fn) {
      case 'storage': {
        if (p.lease !== undefined) { // RENTALS (slice R): the tenant's own storage on a piece of their room
          try { await rentalReducers()!.openRentalStorage({ leaseId: p.lease, key: p.id }); }
          catch (err) { bus.emit('atlas:toast', { text: String((err as Error).message ?? err).replace(/^SenderError:\s*/, ''), kind: 'error' }); return; }
          this.open(p);
          return;
        }
        if (!this.isOwner(p)) { bus.emit('atlas:toast', { text: 'Only the owner may open this storage.', kind: 'error' }); return; }
        const conn = atlas.conn!;
        try {
          await (conn.reducers as unknown as { openContainer(a: { structureId: bigint; key: string; kind: string }): Promise<void> }).openContainer({ structureId: p.structureId!, key: p.id, kind: p.key });
        } catch (err) { bus.emit('atlas:toast', { text: String((err as Error).message ?? err).replace(/^SenderError:\s*/, ''), kind: 'error' }); return; }
        this.open(p);
        return;
      }
      case 'sit': case 'lie': this.beginSeat(p, p.fn); return;
      case 'toggle': this.toggle(p); return;
      case 'craft': bus.emit('atlas:open-workbench', { tab: 'craft' }); return;
      case 'market': bus.emit('atlas:open-workbench', { tab: 'market' }); return;
      case 'passport':
        // The menu bar's own way of opening a panel (a page-body keydown every window listener receives).
        document.body.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyP', key: 'p', bubbles: true, cancelable: true }));
        return;
      case 'music': this.nextTune(); return;
      case 'garden': this.open(p); return;
      case 'guestbook': this.open(p); return;
    }
  }

  // ─── Seats ──────────────────────────────────────────────────────────────────

  private beginSeat(p: Piece, fn: 'sit' | 'lie'): void {
    // THE SEAT WALK TARGETS THE SOCKET (slice C): a planned seat has a collider now, so its centre cannot be reached —
    // the walk goes to the piece's nearest APPROACH point and the body takes the nearest socket. Already at the
    // piece (inside its reach, or on an approach point): sit at once, no walk.
    const near = <T extends { x: number; z: number }>(list: T[] | undefined): T | null => {
      let best: T | null = null, bd = Infinity;
      for (const q of list ?? []) { const d = Math.hypot(q.x - atlas.localX, q.z - atlas.localZ); if (d < bd) { bd = d; best = q; } }
      return best;
    };
    const socket = near(p.sockets), stand = near(p.approach);
    if (socket && (Math.hypot(socket.x - atlas.localX, socket.z - atlas.localZ) < seatReach(p.key) || (stand && Math.hypot(stand.x - atlas.localX, stand.z - atlas.localZ) < 0.6))) {
      this.sitDown(p, fn, socket);
      return;
    }
    this.walking = { piece: p, fn, since: performance.now(), socket, stand: socket ? stand : null };
  }

  /** The last metre: the auto-walk stick carries the server row onto the piece, then the avatar parks. */
  private seatWalk(): void {
    const w = this.walking;
    if (!w) return;
    const input = this.app.getModule<AtlasInputModule>('AtlasInput');
    const tx = w.stand ? w.stand.x : w.piece.x, tz = w.stand ? w.stand.z : w.piece.z; // a planned seat: its approach point
    const dx = tx - atlas.localX, dz = tz - atlas.localZ;
    const d = Math.hypot(dx, dz);
    if (d < 0.22 || performance.now() - w.since > 3000) {
      input?.setAutoWalk(0, 0, 0);
      this.walking = null;
      if (w.socket) { if (Math.hypot(w.socket.x - atlas.localX, w.socket.z - atlas.localZ) < seatReach(w.piece.key) + 0.6) this.sitDown(w.piece, w.fn, w.socket); }
      else if (d < seatReach(w.piece.key)) this.sitDown(w.piece, w.fn); // the walk ends at the collider: the reach follows the piece's size
      return;
    }
    // World → the player's yaw frame (forward = (−sin yaw, −cos yaw), right = (cos yaw, −sin yaw)).
    const yaw = atlas.localYaw;
    const fx = -Math.sin(yaw), fz = -Math.cos(yaw), rx = Math.cos(yaw), rz = -Math.sin(yaw);
    const f = (dx * fx + dz * fz) / d, s = (dx * rx + dz * rz) / d;
    input?.setAutoWalk(f * 0.6, s * 0.6, 0);
  }

  private sitDown(p: Piece, fn: 'sit' | 'lie', socket?: { x: number; z: number; yaw: number } | null): void {
    const pose = seatPose(fn, p.height);
    const sx = socket ? socket.x : p.x, sz = socket ? socket.z : p.z, syaw = socket ? socket.yaw : p.yaw; // a planned piece: the socket (a sofa has two)
    atlas.seated = { x: sx, y: p.y, z: sz, yaw: syaw, eye: pose.eye, bodyScaleY: pose.bodyScaleY, key: p.key, fn };
    atlas.localYaw = syaw + Math.PI; // face the way the piece faces (the avatar's yaw: forward = (−sin, −cos) — π turns it onto the piece's +Z front)
    bus.emit('atlas:reconcile-hold', { ticks: 120, mode: 'hold' }); // the seat parks the avatar; a grace keeps the unstick nudge off the piece
    if (fn === 'lie') {
      const a = atlas as unknown as { breath?: number };
      if (typeof a.breath === 'number') a.breath = 1; // a rest refills the breath
      bus.emit('atlas:status', { text: 'Resting. Any move key stands you up.' });
      // THE REST FEED: the server lifts the Pulse's Rest need once per gap (anyone at the structure — an inn is a bed).
      if (p.structureId !== null) {
        const r = atlas.conn?.reducers as unknown as { restOnBed?(a: { structureId: bigint; key: string }): Promise<void> } | undefined;
        r?.restOnBed?.({ structureId: p.structureId, key: p.id })
          .then(() => bus.emit('atlas:toast', { text: 'Rested — your Rest need rises.', kind: 'success', ms: 2500 }))
          .catch(() => { /* rested a moment ago, or a bed the server does not know: a quiet lie-down */ });
      }
    } else bus.emit('atlas:status', { text: 'Seated. Any move key stands you up.' });
    sfx('sfx_notify', { gain: 0.15 });
  }

  private standUp(): void {
    if (this.walking) { this.app.getModule<AtlasInputModule>('AtlasInput')?.setAutoWalk(0, 0, 0); this.walking = null; }
    if (!atlas.seated) return;
    atlas.seated = null;
    if (this.prompt) this.prompt.style.display = 'none';
  }

  // ─── Toggles and glows ───────────────────────────────────────────────────────

  private toggle(p: Piece): void {
    if (this.toggles.has(p.id)) this.toggles.delete(p.id); else this.toggles.add(p.id);
    sfx('sfx_notify', { gain: 0.15 });
  }

  private tickGlows(): void {
    const want = new Set<string>();
    for (const p of this.pieces) if (p.fn === 'toggle' && this.toggles.has(p.id)) want.add(p.id);
    for (const [id, sp] of this.glows) if (!want.has(id)) { this.scene.remove(sp); this.glows.delete(id); }
    for (const p of this.pieces) {
      if (!want.has(p.id) || this.glows.has(p.id)) continue;
      if (!this.glowMat) {
        const c = document.createElement('canvas'); c.width = c.height = 64;
        const g = c.getContext('2d')!; const gr = g.createRadialGradient(32, 32, 2, 32, 32, 32);
        gr.addColorStop(0, 'rgba(255,214,140,0.95)'); gr.addColorStop(0.45, 'rgba(255,190,90,0.45)'); gr.addColorStop(1, 'rgba(255,170,60,0)');
        g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
        const tex = new THREE.CanvasTexture(c);
        this.glowMat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
      }
      const sp = new THREE.Sprite(this.glowMat);
      sp.scale.setScalar(Math.max(0.6, p.height * 0.9));
      sp.position.set(p.x, p.y + p.height * 0.75, p.z);
      sp.renderOrder = 990;
      this.scene.add(sp);
      this.glows.set(p.id, sp);
    }
    if (!atlas.interiorId && this.glows.size) { for (const sp of this.glows.values()) this.scene.remove(sp); this.glows.clear(); }
  }

  // ─── The orchestrion ─────────────────────────────────────────────────────────

  private nextTune(): void {
    this.jukeboxIdx = (this.jukeboxIdx + 1) % (JUKEBOX_TRACKS.length + 1);
    const key = this.jukeboxIdx < JUKEBOX_TRACKS.length ? JUKEBOX_TRACKS[this.jukeboxIdx] : null;
    if (key === null) this.jukeboxIdx = -1;
    bus.emit('atlas:jukebox', { key });
    bus.emit('atlas:toast', { text: key ? `Orchestrion: ${key.replace(/^music_/, '').replace(/_/g, ' ')}` : 'Orchestrion: silent', kind: 'info', ms: 2500 });
  }

  // ─── The windows (storage · garden · guest book) ─────────────────────────────

  private open(p: Piece): void {
    if (!this.win) {
      this.win = makeWindow({ id: 'furniture', title: 'Furniture', icon: 'build', width: 640, height: 520, minWidth: 460, minHeight: 320 });
      this.win.body.style.cssText += 'display:flex;flex-direction:column;font:13px var(--ax-font);color:var(--ax-text);overflow:auto;padding:10px 12px;gap:10px;';
    }
    this.winPiece = p;
    this.win.setOpen(true);
    this.render();
  }

  private scheduleRender(): void {
    if (!this.win?.isOpen()) return;
    if (this.renderTimer !== null) return;
    this.renderTimer = window.setTimeout(() => { this.renderTimer = null; this.render(); }, 120);
  }

  private render(): void {
    const win = this.win, p = this.winPiece;
    if (!win || !p) return;
    const name = FURNITURE[p.key]?.name ?? p.key;
    win.setTitle(name);
    // SOFT RENDER (2026-09-25): every playerItem / playerResource / container row anyone touched rebuilt the window —
    // a quantity or a guest-book line being typed was wiped. The fresh tree is patched over the live one.
    softRender(win.body, (body) => {
      if (p.fn === 'storage') this.renderStorage(body, p, name);
      else if (p.fn === 'garden') this.renderGarden(body, p, name);
      else if (p.fn === 'guestbook') this.renderGuestbook(body, p, name);
    });
  }

  /** The quantity field on screen for `k` (a kept field holds what the player typed), else the one just built. */
  private liveField(k: string, fallback: HTMLInputElement): HTMLInputElement { return fieldByKey(this.win?.body, k) ?? fallback; }

  private call(label: string, fn: () => Promise<unknown>): void {
    fn().then(() => { if (label) bus.emit('atlas:toast', { text: label, kind: 'success', ms: 2000 }); this.scheduleRender(); })
      .catch((err: Error) => bus.emit('atlas:toast', { text: String(err?.message ?? err).replace(/^SenderError:\s*/, '').slice(0, 160), kind: 'error' }));
  }

  /** RENTALS (slice R): the TENANT's storage on a piece of their let room — `rental_container` / `rental_stored`,
   *  rows only the tenant is ever handed; the furniture storage's own layout, the rental reducers behind it. */
  private renderTenantStorage(body: HTMLElement, p: Piece, name: string): void {
    const conn = atlas.conn!, r = rentalReducers()!;
    const db = conn.db as unknown as { playerItem: { iter(): Iterable<PlayerItemRow> }; playerResource: { iter(): Iterable<StackRow> } };
    const container = tenantContainer(p.lease!, p.id);
    if (!container) { body.appendChild(this.text('Opening…', 'var(--ax-muted)')); return; }
    const entries = tenantEntries(container.containerId);
    body.appendChild(this.heading(`${name} · ${entries.length} / ${container.capacity} slots${CONTAINER_FILTER[p.key] ? ' · weapons only' : ''} · your room's storage — yours alone while the lease stands`));
    const inside = this.section('INSIDE');
    if (entries.length === 0) inside.appendChild(this.text('Empty.', 'var(--ax-muted)'));
    for (const e of entries) {
      const label = this.text(e.kind === 'stack' ? `${e.name} × ${e.qty} (${e.itemKey})` : `${e.name}${e.quality ? ` · q${e.quality}` : ''}`, 'var(--ax-text)'); label.style.flex = '1';
      label.setAttribute('data-entry', e.entryId.toString());
      let row: HTMLElement;
      if (e.kind === 'stack') {
        const q = this.qtyInput(e.qty);
        const qk = `take:${e.entryId}`;
        q.setAttribute('data-k', qk);
        row = this.row(label, q, this.button('Take', () => this.call('', () => r.takeRentalStack({ entryId: e.entryId, qty: Math.max(1, Math.min(e.qty, Math.floor(Number(this.liveField(qk, q).value)) || e.qty)) }))));
      } else row = this.row(label, this.button('Take', () => this.call('', () => r.takeRentalItem({ entryId: e.entryId }))));
      row.setAttribute('data-row', `entry:${e.entryId}`);
      inside.appendChild(row);
    }
    body.appendChild(inside);
    const mine = this.section('YOUR INVENTORY');
    const who = conn.identity!;
    const items = [...db.playerItem.iter()].filter((i) => i.owner.isEqual(who));
    const stacks = CONTAINER_FILTER[p.key] ? [] : [...db.playerResource.iter()].filter((st) => st.owner.isEqual(who));
    if (items.length === 0 && stacks.length === 0) mine.appendChild(this.text('Nothing to store.', 'var(--ax-muted)'));
    for (const it of items) {
      const label = this.text(`${it.name}${it.quality ? ` · q${it.quality}` : ''}`, 'var(--ax-text)'); label.style.flex = '1';
      label.setAttribute('data-item', it.itemId.toString());
      const row = this.row(label, this.button('Store', () => this.call('', () => r.storeRentalItem({ containerId: container.containerId, playerItemId: it.itemId }))));
      row.setAttribute('data-row', `item:${it.itemId}`);
      mine.appendChild(row);
    }
    for (const st of stacks) {
      const label = this.text(`${st.name} × ${st.qty} (${st.familyKey})`, 'var(--ax-text)'); label.style.flex = '1';
      const q = this.qtyInput(st.qty);
      const qk = `store:${st.stackId}`;
      q.setAttribute('data-k', qk);
      const row = this.row(label, q, this.button('Store', () => this.call('', () => r.storeRentalStack({ containerId: container.containerId, stackId: st.stackId, qty: Math.max(1, Math.min(st.qty, Math.floor(Number(this.liveField(qk, q).value)) || st.qty)) }))));
      row.setAttribute('data-row', `stack:${st.stackId}`);
      mine.appendChild(row);
    }
    body.appendChild(mine);
    body.appendChild(this.text('When the lease ends — you leave, the rent lapses past its grace, the landlord\'s notice runs out, or the building is taken down — everything here returns to your inventory.', 'var(--ax-muted)'));
  }

  private renderStorage(body: HTMLElement, p: Piece, name: string): void {
    if (p.lease !== undefined) { this.renderTenantStorage(body, p, name); return; }
    const conn = atlas.conn!;
    const db = conn.db as unknown as {
      structureContainer: { iter(): Iterable<ContainerRow> }; containerItem: { iter(): Iterable<EntryRow> };
      playerItem: { iter(): Iterable<PlayerItemRow> }; playerResource: { iter(): Iterable<StackRow> };
    };
    const container = [...db.structureContainer.iter()].find((c) => c.structureId === p.structureId && c.key === p.id);
    if (!container) { body.appendChild(this.text('Opening…', 'var(--ax-muted)')); return; }
    const entries = [...db.containerItem.iter()].filter((e) => e.containerId === container.containerId);
    const r = conn.reducers as unknown as {
      storeItem(a: { containerId: bigint; playerItemId: bigint }): Promise<void>;
      storeStack(a: { containerId: bigint; stackId: bigint; qty: number }): Promise<void>;
      takeItem(a: { entryId: bigint }): Promise<void>;
      takeStack(a: { entryId: bigint; qty: number }): Promise<void>;
    };
    body.appendChild(this.heading(`${name} · ${entries.length} / ${container.capacity} slots${CONTAINER_FILTER[p.key] ? ' · weapons only' : ''} · yours alone`));
    const inside = this.section('INSIDE');
    if (entries.length === 0) inside.appendChild(this.text('Empty.', 'var(--ax-muted)'));
    for (const e of entries) {
      const label = this.text(e.kind === 'stack' ? `${e.name} × ${e.qty} (${e.itemKey})` : `${e.name}${e.quality ? ` · q${e.quality}` : ''}`, 'var(--ax-text)'); label.style.flex = '1';
      label.setAttribute('data-entry', e.entryId.toString());
      let row: HTMLElement;
      if (e.kind === 'stack') {
        const q = this.qtyInput(e.qty);
        const qk = `take:${e.entryId}`;
        q.setAttribute('data-k', qk);
        row = this.row(label, q, this.button('Take', () => this.call('', () => r.takeStack({ entryId: e.entryId, qty: Math.max(1, Math.min(e.qty, Math.floor(Number(this.liveField(qk, q).value)) || e.qty)) }))));
      } else row = this.row(label, this.button('Take', () => this.call('', () => r.takeItem({ entryId: e.entryId }))));
      row.setAttribute('data-row', `entry:${e.entryId}`); // rows pair by identity in the soft render
      inside.appendChild(row);
    }
    body.appendChild(inside);
    const mine = this.section('YOUR INVENTORY');
    const me = conn.identity!;
    const items = [...db.playerItem.iter()].filter((i) => i.owner.isEqual(me));
    const stacks = CONTAINER_FILTER[p.key] ? [] : [...db.playerResource.iter()].filter((s) => s.owner.isEqual(me));
    if (items.length === 0 && stacks.length === 0) mine.appendChild(this.text('Nothing to store.', 'var(--ax-muted)'));
    for (const it of items) {
      const label = this.text(`${it.name}${it.quality ? ` · q${it.quality}` : ''}`, 'var(--ax-text)'); label.style.flex = '1';
      label.setAttribute('data-item', it.itemId.toString());
      const row = this.row(label, this.button('Store', () => this.call('', () => r.storeItem({ containerId: container.containerId, playerItemId: it.itemId }))));
      row.setAttribute('data-row', `item:${it.itemId}`);
      mine.appendChild(row);
    }
    for (const st of stacks) {
      const label = this.text(`${st.name} × ${st.qty} (${st.familyKey})`, 'var(--ax-text)'); label.style.flex = '1';
      const q = this.qtyInput(st.qty);
      const qk = `store:${st.stackId}`;
      q.setAttribute('data-k', qk);
      const row = this.row(label, q, this.button('Store', () => this.call('', () => r.storeStack({ containerId: container.containerId, stackId: st.stackId, qty: Math.max(1, Math.min(st.qty, Math.floor(Number(this.liveField(qk, q).value)) || st.qty)) }))));
      row.setAttribute('data-row', `stack:${st.stackId}`); // a stack's identity is in the markup (two alike stacks never share a kept button)
      mine.appendChild(row);
    }
    body.appendChild(mine);
  }

  private renderGarden(body: HTMLElement, p: Piece, name: string): void {
    const conn = atlas.conn!;
    const planter = this.planterOf(p);
    const owner = this.isOwner(p);
    const r = conn.reducers as unknown as {
      plantSeed(a: { structureId: bigint; key: string; stackId: bigint }): Promise<void>;
      harvestPlanter(a: { planterId: bigint }): Promise<void>;
    };
    body.appendChild(this.heading(`${name}${owner ? '' : ' · the owner tends it'}`));
    if (planter) {
      const left = Math.max(0, Number(planter.readyAt.microsSinceUnixEpoch / 1000n) - Date.now());
      const ready = left <= 0;
      body.appendChild(this.text(ready ? `${planter.name} is ready — ${PLANTER_YIELD} units to harvest.` : `${planter.name} growing — ${Math.ceil(left / 60000)} min to go.`, ready ? 'var(--ax-good)' : 'var(--ax-text)')); // THE TOKEN LAW (2026-09-25): was a hex literal
      if (owner) body.appendChild(this.row(this.button('Harvest', () => this.call('Harvested.', () => r.harvestPlanter({ planterId: planter.planterId })), ready)));
      return;
    }
    body.appendChild(this.text('Nothing planted. Plant one unit of a food family; it grows in 4 h and yields three.', 'var(--ax-muted)'));
    if (!owner) return;
    const db = conn.db as unknown as { playerResource: { iter(): Iterable<StackRow> } };
    const food = new Set(['grain', 'produce', 'game', 'brine_catch']);
    const stacks = [...db.playerResource.iter()].filter((s) => s.owner.isEqual(conn.identity!) && food.has(s.familyKey));
    if (stacks.length === 0) body.appendChild(this.text('You carry no food family to plant.', 'var(--ax-muted)'));
    for (const st of stacks) {
      const label = this.text(`${st.name} × ${st.qty} (${st.familyKey})`, 'var(--ax-text)'); label.style.flex = '1';
      const row = this.row(label, this.button('Plant one', () => this.call('Planted.', () => r.plantSeed({ structureId: p.structureId!, key: p.id, stackId: st.stackId }))));
      row.setAttribute('data-row', `stack:${st.stackId}`);
      body.appendChild(row);
    }
  }

  private renderGuestbook(body: HTMLElement, p: Piece, name: string): void {
    const conn = atlas.conn!;
    const db = conn.db as unknown as { structureNote?: { iter(): Iterable<NoteRow> } };
    const notes = db.structureNote ? [...db.structureNote.iter()].filter((n) => n.structureId === p.structureId).sort((a, b) => Number(b.at.microsSinceUnixEpoch - a.at.microsSinceUnixEpoch)) : [];
    body.appendChild(this.heading(`${name} · ${notes.length} line${notes.length === 1 ? '' : 's'}`));
    const list = this.section('THE BOOK');
    if (notes.length === 0) list.appendChild(this.text('No one has signed yet.', 'var(--ax-muted)'));
    for (const n of notes) {
      const line = this.text(`${n.authorName}: ${n.text}`, 'var(--ax-text)');
      line.setAttribute('data-note', n.noteId.toString());
      list.appendChild(line);
    }
    body.appendChild(list);
    const inp = document.createElement('input'); inp.type = 'text'; inp.maxLength = 120; inp.placeholder = 'Leave a line…';
    inp.style.cssText = 'flex:1;min-width:200px;padding:6px 8px;border-radius:var(--ax-radius);border:1px solid var(--ax-line2);background:var(--ax-bg2);color:var(--ax-text);font:13px var(--ax-font)';
    inp.setAttribute('data-k', 'note');
    const r = conn.reducers as unknown as { signGuestbook(a: { structureId: bigint; text: string }): Promise<void> };
    body.appendChild(this.row(inp, this.button('Sign', () => {
      // The field ON SCREEN (a kept field holds the line being written); cleared once the book takes it — the soft
      // render keeps a field's text, so the rebuild no longer clears it for us (2026-09-25).
      const f = this.liveField('note', inp);
      const t = f.value.trim();
      if (t) this.call('Signed.', () => r.signGuestbook({ structureId: p.structureId!, text: t }).then(() => { if (f.value.trim() === t) f.value = ''; }));
    })));
  }

  // ─── DOM helpers ────────────────────────────────────────────────────────────

  private heading(text: string): HTMLElement { const d = document.createElement('div'); d.textContent = text; d.style.cssText = 'font-weight:600;color:var(--ax-hi);letter-spacing:.03em'; return d; }
  private section(title: string): HTMLElement {
    const d = document.createElement('div'); d.style.cssText = 'display:flex;flex-direction:column;gap:6px;padding:8px 10px;border:1px solid var(--ax-line);border-radius:var(--ax-radius);background:var(--ax-bg2)';
    const t = document.createElement('div'); t.textContent = title; t.style.cssText = 'font-size:11px;color:var(--ax-hi);letter-spacing:.06em'; d.appendChild(t); return d;
  }
  private row(...els: HTMLElement[]): HTMLElement { const d = document.createElement('div'); d.style.cssText = 'display:flex;align-items:center;gap:8px;flex-wrap:wrap'; for (const e of els) d.appendChild(e); return d; }
  private text(text: string, color: string): HTMLElement { const d = document.createElement('div'); d.textContent = text; d.style.color = color; return d; }
  private qtyInput(max: number): HTMLInputElement {
    const i = document.createElement('input'); i.type = 'number'; i.min = '1'; i.max = String(max); i.value = String(max);
    i.style.cssText = 'width:72px;padding:4px 6px;border-radius:var(--ax-radius);border:1px solid var(--ax-line2);background:var(--ax-bg2);color:var(--ax-text);font:13px var(--ax-font)'; return i;
  }
  private button(label: string, onClick: () => void, enabled = true): HTMLButtonElement {
    const b = document.createElement('button'); b.textContent = label; b.disabled = !enabled;
    b.style.cssText = 'padding:5px 12px;border-radius:var(--ax-radius);border:1px solid var(--ax-line2);background:var(--ax-bg3);color:var(--ax-text);font:13px var(--ax-font);cursor:pointer';
    if (!enabled) b.style.opacity = '0.5';
    b.addEventListener('click', onClick); return b;
  }

  /** Probe lever. */
  private diag(): unknown {
    return {
      pieces: this.pieces.map((p) => ({ id: p.id, key: p.key, fn: p.fn, x: p.x, y: p.y, z: p.z, d: Math.hypot(p.x - atlas.localX, p.z - atlas.localZ), offer: this.offerDist(p), approach: p.approach ?? null, sockets: p.sockets ?? null })),
      nearest: this.nearest ? { id: this.nearest.id, key: this.nearest.key, fn: this.nearest.fn } : null,
      promptShown: !!this.prompt && this.prompt.style.display !== 'none',
      promptText: this.prompt?.textContent ?? '',
      seated: atlas.seated,
      walking: !!this.walking,
      toggles: [...this.toggles],
      glows: this.glows.size,
      jukebox: this.jukeboxIdx,
      open: !!this.win?.isOpen(),
      focus: interactFocus().focus(),
      use: (id: string) => { const p = this.pieces.find((q) => q.id === id); if (p) void this.use(p); return !!p; },
      standAt: async (id: string, back = 1.2) => { // a probe: the row moves too
        const p = this.pieces.find((q) => q.id === id); if (!p) return false;
        const x = p.x - Math.sin(p.yaw) * back, z = p.z - Math.cos(p.yaw) * back;
        try { await (atlas.conn!.reducers as unknown as { debugTeleport(a: { posX: number; posZ: number }): Promise<void> }).debugTeleport({ posX: x, posZ: z }); } catch { /* no flag */ }
        atlas.localX = x; atlas.localZ = z; this.scanAt = 0; return true;
      },
      standUp: () => this.standUp(),
      rescan: () => { this.scanAt = 0; this.scan(); return this.pieces.length; },
      close: () => this.win?.setOpen(false),
    };
  }

  dispose(): void {
    this.standUp();
    window.removeEventListener('keydown', this.onKey);
    this.prompt?.remove();
    for (const sp of this.glows.values()) this.scene.remove(sp);
    this.glows.clear();
  }
}
